import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LayoutGrid, ListTree, Settings, Radio, Handshake, Building2, HeartHandshake, Disc3, Inbox } from "lucide-react";
import { getLeads, money } from "../lib/api";

const NAV = [
  { to: "/control", label: "Inquiries", icon: Inbox, end: true },
  { to: "/control/leads", label: "Artists & Leads", icon: ListTree },
  { to: "/control/providers", label: "Service Providers", icon: Handshake },
  { to: "/control/partner-orgs", label: "Partners", icon: Building2 },
  { to: "/control/volunteers", label: "Volunteers", icon: HeartHandshake },
  { to: "/control/labels", label: "Labels & Industry", icon: Disc3 },
  { to: "/control/catalog", label: "Service Catalog", icon: LayoutGrid },
  { to: "/control/notion", label: "Notion Sync", icon: Settings },
];

export function ControlLayout() {
  return (
    <div className="grain min-h-screen bg-black">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-zinc-900 bg-black/80 px-6 py-4 backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="control-brand">
            <img src="/logo-dark.webp" alt="Sabrea Soundworks" className="h-8 w-8 rounded-full" />
            <span className="font-display text-base font-black text-zinc-50">Sabrea Soundworks</span>
          </Link>
          <span className="font-mono-tech text-[10px] text-zinc-500">Control Center</span>
        </div>
        <Link to="/" data-testid="new-intake-link" className="font-mono-tech text-[10px] text-brand-pink hover:text-brand-pink-soft transition-colors duration-200">+ New application</Link>
      </header>
      <div className="md:grid md:grid-cols-[240px_1fr]">
        <nav className="border-b border-zinc-900 md:min-h-[calc(100vh-61px)] md:border-b-0 md:border-r">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 border-b border-zinc-900 px-6 py-4 font-mono-tech text-[11px] transition-colors duration-200 ${isActive ? "bg-zinc-900/60 text-brand-pink" : "text-zinc-400 hover:bg-zinc-900/40 hover:text-zinc-100"}`}>
              <n.icon className="h-3.5 w-3.5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}

const SEVERITY = { block: "text-red-400 border-red-500/40 bg-red-500/10", warn: "text-brand-pink border-brand-pink/40 bg-brand-pink/10", info: "text-brand-indigo border-brand-indigo/40 bg-brand-indigo/10" };

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLeads(filter === "review" ? { review: true } : {}).then(setLeads).finally(() => setLoading(false));
  }, [filter]);

  const shown = filter === "all" || filter === "review" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-display text-3xl font-black text-zinc-50">Leads & Intakes</h1>
      <p className="mt-2 text-sm text-zinc-500">Every submitted Get Started application with its deterministic routing outcome.</p>

      <div className="mt-8 flex flex-wrap gap-2">
        {["all", "review", "Submitted", "A&R Reviewed", "Discovery Booked", "Qualified", "Closed Won", "Closed Lost"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-2 font-mono-tech text-[10px] transition-colors duration-200 ${filter === f ? "border-brand-pink bg-brand-pink text-zinc-950" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
            {f === "review" ? "Needs review" : f}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[["Total", leads.length], ["Needs review", leads.filter((l) => l.human_review_required).length], ["Quoting blocked", leads.filter((l) => l.quoting_blocked).length]].map(([k, v]) => (
          <div key={k} className="border border-zinc-900 bg-zinc-900/40 p-6" data-testid={`stat-${k}`}>
            <p className="font-mono-tech text-[10px] text-zinc-500">{k}</p>
            <p className="mt-2 font-display text-3xl font-black text-zinc-50">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 border-t border-zinc-900" data-testid="leads-table">
        {loading && <p className="py-8 font-mono-tech text-[11px] text-zinc-500">Loading…</p>}
        {!loading && shown.length === 0 && (
          <p className="py-10 text-sm text-zinc-500">No applications yet. <Link to="/" className="text-brand-pink">Complete the Get Started flow</Link> to see routing in action.</p>
        )}
        {shown.map((l) => (
          <Link key={l.id} to={`/control/leads/${l.id}`} data-testid={`lead-row-${l.id}`}
            className="grid grid-cols-1 gap-3 border-b border-zinc-900 px-1 py-5 transition-colors duration-200 hover:bg-zinc-900/50 md:grid-cols-[1.5fr_1.5fr_1fr_1fr_auto] md:items-center">
            <div>
              <p className="font-display font-bold text-zinc-50">{l.artist_name}</p>
              <p className="font-mono-tech text-[10px] text-zinc-500">{l.type} · {l.created_at?.slice(0, 10)}</p>
            </div>
            <p className="text-sm text-zinc-400">{l.primary_need}</p>
            <p className="font-mono-tech text-[10px] text-zinc-400">{l.price_low === null ? "Quoting blocked" : `${money(l.price_low)}–${money(l.price_high)}`}</p>
            <p className="font-mono-tech text-[10px] text-brand-indigo">{l.status}</p>
            <div className="flex flex-wrap gap-1.5">
              {(l.flags || []).map((f) => (
                <span key={f.code} className={`rounded-sm border px-2 py-1 font-mono-tech text-[9px] ${SEVERITY[f.severity]}`}>{f.code}</span>
              ))}
              {l.notion?.synced && <span className="rounded-sm border border-green-500/40 bg-green-500/10 px-2 py-1 font-mono-tech text-[9px] text-green-400"><Radio className="inline h-2.5 w-2.5" /> SYNCED</span>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
